// LA source de vérité des clés de traduction (Phase 95).
//
// Ce fichier est importé STATIQUEMENT et ne peut pas l'être autrement : il est
// à la fois le repli de `translate()` (toute clé absente d'un catalogue
// étranger retombe ici) et le typage du catalogue anglais (`MsgKey = keyof
// typeof fr`). Le rendre paresseux casserait les deux.
//
// Le figeage littéral en fin d'objet est structurel, pas cosmétique : sans lui
// les clés seraient de simples `string` et `MsgKey` perdrait tout pouvoir de
// détection.
//
// Voie d'agrandissement (Phase 97) : à ~900 chaînes ce fichier dépassera
// ~10 Ko gzip dans le chunk d'entrée. Le découper alors par page
// (`fr.home.ts`, `fr.reserve.ts`, …) pour que les chaînes de chaque page
// retrouvent le chunk de sa route.
// Refs: phase 95.

export const fr = {
  'lang.fr': 'Français',
  'lang.en': 'English',
  'lang.switcher': 'Langue',

  // Chrome partagé (Header / Footer). Les valeurs françaises sont des copies
  // EXACTES des chaînes JSX d'origine : un « nettoyage » au passage casserait
  // un test existant et ferait croire à une régression du socle.
  'nav.concept': 'Concept',
  'nav.reserve': 'Réserver',
  'nav.pricing': 'Tarifs',
  'nav.packs': 'Packs',
  'nav.access': 'Accès',
  'nav.blog': 'Blog',
  'nav.contact': 'Contact',
  'nav.menu': 'Menu',
  'account.menu': 'Menu compte',
  'account.login': 'Se connecter',
  'account.myAccount': 'Mon compte',
  'account.myInvoices': 'Mes factures',
  'account.myDashboard': 'Mon dashboard',
  'account.admin': 'Espace admin',
  'account.signOut': 'Déconnexion',
  'footer.legal': 'Mentions légales',

  // Page inconnue (Phase 96, plan 96-05). Tutoiement, comme tout le site.
  'notFound.title': 'Page introuvable',
  'notFound.body': "Le lien que tu as suivi n'existe pas ou a été déplacé.",
  'notFound.home': "Retour à l'accueil",

  // Bandeau « Switch to English » (D-07). Les trois valeurs sont en ANGLAIS
  // dans les DEUX catalogues, et ce n'est pas un oubli de traduction : le
  // bandeau ne se rend QUE sur une page française, et ne s'adresse qu'à
  // quelqu'un dont la langue suggérée est l'anglais. Le traduire en français
  // reviendrait à écrire une proposition que son destinataire ne lit pas.
  'enBanner.text': 'This page is also available in English.',
  'enBanner.cta': 'Switch to English',
  'enBanner.dismiss': 'Dismiss',

  // Écran d'erreur de dernier recours (ErrorBoundaryFallback, monté par
  // main.tsx sous TOUTES les pages). Les valeurs françaises sont des copies
  // exactes des chaînes JSX d'origine, ponctuation comprise. La référence
  // d'incident est UNE clé interpolée : la ponctuation autour du numéro n'est
  // pas la même d'une langue à l'autre (« Réf. : » contre « Ref: »), et une
  // concaténation maison la figerait sur la française.
  'errorBoundary.title': 'Une erreur est survenue',
  'errorBoundary.body': 'Réessayez, ou contactez-nous.',
  'errorBoundary.reload': 'Recharger la page',
  'errorBoundary.report': 'Signaler ce problème',
  'errorBoundary.ref': 'Réf. : {ref}',

  // Bandeau d'activation des notifications (PushOptIn, monté par
  // SlotConfirmation). Le composant a DEUX rendus — renvoi vers /install hors
  // mode autonome, activation en mode autonome — qui partagent le même titre et
  // le même corps : UNE clé chacun, pas deux copies à faire diverger. Le refus
  // de permission est un toast posé sur document.body, hors du <main> balayé
  // par le harnais D-04.
  'push.title': 'Reste informé·e de tes prochaines séances',
  'push.body': 'Active les notifications pour recevoir un rappel la veille de chaque séance.',
  'push.installFirst': "Installer d'abord l'application",
  'push.activate': 'Activer les notifications',
  'push.dismiss': 'Non merci',
  'push.denied':
    'Notifications refusées — tu peux les activer depuis les paramètres de ton navigateur.',

  // Page de réservation (plan 95-05). La phrase portant la durée est UNE clé
  // interpolée, pas trois fragments concaténés : une phrase coupée en morceaux
  // ne se réordonne pas d'une langue à l'autre. Le sélecteur de créneaux monté
  // par cette page relève du plan 95-06 et n'a délibérément aucune clé ici.
  'reserve.eyebrow': 'RÉSERVATION',
  'reserve.title': 'Choisis ton créneau',
  'reserve.subtitle': 'Séance prépayée de {hours}h — accès studio autonome.',

  // Sélecteur de créneau (plan 95-06). Valeurs françaises recopiées À L'OCTET
  // depuis le JSX d'origine, apostrophes typographiques et tirets cadratins
  // compris : c'est ce qui garde verts, sans les toucher, les blocs de test qui
  // épinglent ces chaînes.
  'reserve.pendingHold': 'Tu as une pré-réservation en attente sur',
  'reserve.pendingHoldExpiry': '(expire à {time})',
  'reserve.resume': 'Reprendre',
  'reserve.resumeAria': 'Reprendre la pré-réservation et finaliser le paiement',
  'reserve.release': 'Libérer',
  'reserve.releaseAria': 'Libérer la pré-réservation',
  'reserve.phoneLabel': 'Ton numéro (facultatif)',
  'reserve.phoneDialAria': 'Indicatif pays',
  'reserve.phoneSaved': 'Numéro enregistré ✓',
  'reserve.phoneHint':
    "Pour qu'on puisse te contacter simplement en cas de problème, et plus tard recevoir tes codes directement sur WhatsApp.",
  'reserve.holdNotice': 'Le créneau choisi est tenu 15 min — finalise le paiement dans ce délai.',
  'reserve.slotsLoading': 'Chargement des créneaux…',
  'reserve.noSlotsTitle': 'Aucun créneau disponible',
  'reserve.noSlotsCoaching':
    'Aucun créneau réservable ce jour-là (le coaching se réserve au moins 48h à l’avance) — essaie une date plus lointaine.',
  'reserve.noSlotsDay': 'Aucun créneau disponible ce jour-là — essaie une autre date ou contacte-nous.',
  'reserve.slotsGroupAria': 'Créneaux disponibles le {date}',
  'reserve.reserving': 'Réservation en cours…',
  'reserve.pickDate': '← Sélectionne une date',
  'reserve.selectedSlot': 'Créneau sélectionné :',
  'reserve.holdCountdown': 'Créneau réservé pour toi — finalise avant {time}',
  'reserve.promoLabel': 'Code promo (optionnel)',
  // Le prix arrive DÉJÀ formaté par money.ts — la clé ne connaît ni le symbole
  // ni sa position, qui changent d'une langue à l'autre.
  'reserve.pay': 'Payer ({price})',
  'reserve.packCreditsNeeded_one': 'Il te faut {count} crédit (solde : {balance})',
  'reserve.packCreditsNeeded_other': 'Il te faut {count} crédits (solde : {balance})',
  // Paire `_one` / `_other` choisie par `pluralKey` (plan 95-09) — le ternaire
  // d'attente a disparu. Le suffixe est celui d'ICU, pas une convention maison.
  'reserve.usePackCredit_one': 'Utiliser {count} crédit pack',
  'reserve.usePackCredit_other': 'Utiliser {count} crédits pack',
  'reserve.confirming': 'Confirmation…',
  'reserve.redirecting': 'Redirection…',
  // Messages destinés au visiteur, y compris ceux mappés depuis un code
  // serveur. Les CODES eux-mêmes (`not_held`, `slot_taken`, `hold_expired`)
  // restent en anglais : ce sont des identifiants techniques, pas de l'affichage.
  'reserve.slotsLoadError': 'Impossible de charger les créneaux. Vérifie ta connexion et réessaie.',
  'reserve.holdExpiredPickNew': 'Ta pré-réservation a expiré — choisis un nouveau créneau.',
  'reserve.holdReleased': 'Pré-réservation libérée — le créneau est de nouveau disponible.',
  'reserve.holdNoLongerPending':
    "Cette pré-réservation n'est plus en attente — elle a déjà été confirmée ou libérée.",
  'reserve.holdReleaseFailed': 'Impossible de libérer la pré-réservation. Réessaie.',
  'reserve.slotExpired': 'Ton créneau a expiré — choisis-en un autre.',
  'reserve.slotJustTaken': "Ce créneau vient d'être pris. Choisis-en un autre.",
  'reserve.reserveFailed': 'Impossible de réserver ce créneau. Réessaie.',
  'reserve.confirmFailed': 'Impossible de confirmer la réservation. Réessaie.',

  // Page d'accueil (plan 95-07). Le bloc le plus dense du catalogue : c'est lui
  // qui met la mécanique à l'épreuve du volume. Valeurs recopiées À L'OCTET du
  // JSX d'origine — apostrophes typographiques (’) là où le JSX en avait,
  // droites (') là où il en avait, tirets cadratins, espaces et casse compris.
  // Une « uniformisation » au passage casserait une des 17 assertions
  // françaises de Home.render.test.tsx et ferait croire à une régression.
  //
  // Aucun montant en dur : les prix arrivent formatés par money.ts, donc la
  // clé ne connaît ni le symbole ni sa position.
  'home.cancel.message': 'Paiement annulé. Tu peux réessayer quand tu le souhaites.',
  'home.cancel.close': 'Fermer la notification',

  // Le titre du hero est en TROIS fragments parce qu'un mot y est colorisé par
  // un <span> — et que dangerouslySetInnerHTML est refusé sur cette page
  // (T-95-06). Chaque langue place son propre mot accentué au milieu de sa
  // propre phrase : c'est trois fragments par langue, pas un ordre imposé
  // d'une langue à l'autre. L'espace entre fragments vit dans le JSX.
  'home.hero.eyebrow': 'Studio de coaching privé',
  'home.hero.titleLead': 'Ton studio privé',
  'home.hero.titleAccent': 'autonome',
  'home.hero.titleTail': 'à Paris',
  'home.hero.subtitle':
    'Accès PIN 24/7. Sans personnel. Réserve un créneau, reçois ton code, entraîne-toi. Seul ou avec un coach.',
  'home.hero.ctaSession': 'Séance privée — {price}',
  'home.hero.ctaTrial': 'Première séance — {price}',

  'home.value.srTitle': 'Pourquoi SweatStudio',
  'home.value.private.title': 'Privé',
  'home.value.private.desc':
    'Un espace rien qu’à toi. Pas de regard extérieur, pas d’attente, pas de compromis.',
  'home.value.flexible.title': 'Flexible',
  'home.value.flexible.desc':
    'Réserve quand tu veux — 6h du matin ou 23h. Zéro engagement, zéro abonnement.',
  'home.value.premium.title': 'Premium',
  'home.value.premium.desc':
    'Équipement professionnel, ambiance soignée. Le studio des coachs qui veulent le meilleur pour leurs clients.',

  'home.how.eyebrow': 'Comment ça marche',
  'home.how.title': 'En trois étapes',
  'home.how.pick.title': 'Choisis un créneau',
  'home.how.pick.desc': 'Sélectionne l’horaire qui te convient dans le calendrier disponible.',
  'home.how.pay.title': 'Paie en ligne',
  'home.how.pay.desc': 'Paiement sécurisé Stripe. {price} l’heure, sans engagement.',
  'home.how.pin.title': 'Reçois ton code PIN',
  'home.how.pin.desc':
    'Code d’accès envoyé par email dès la réservation. Il devient valable 15 minutes avant l’heure de ta séance.',

  'home.booking.eyebrow': 'Réservation',
  'home.booking.title': 'Réserve ton créneau',
  'home.booking.lead':
    'Sélectionne une date, choisis un créneau, paie en ligne — ton code PIN arrive par email.',

  'home.gallery.eyebrow': 'Galerie',
  'home.gallery.title': 'Le studio en images',
  'home.gallery.lead': 'Espace, matériel pro et douche sur place — un aperçu avant de réserver.',

  'home.pricing.eyebrow': 'Tarifs',
  'home.pricing.title': 'Tarifs',
  'home.pricing.lead': 'Simples, transparents, sans engagement.',
  'home.pricing.trial.eyebrow': 'Offre découverte',
  'home.pricing.trial.title': 'Première séance',
  'home.pricing.trial.desc': 'Découvre le studio à tarif découverte — une seule fois.',
  'home.pricing.trial.unit': 'la séance',
  'home.pricing.trial.ctaBook': 'Réserver ma première séance',
  'home.pricing.trial.ctaSignup': 'Créer un compte',
  'home.pricing.session.title': 'Séance privée',
  'home.pricing.session.desc':
    'Le studio en autonomie, sans coach. Équipement professionnel à disposition.',
  'home.pricing.session.unit': '/ heure',
  'home.pricing.session.packHint': "Ou prends un pack de séances : jusqu'à −36 % (voir plus bas).",
  'home.pricing.session.cta': 'Réserver une séance',
  'home.pricing.coaching.title': 'Coaching personnalisé',
  'home.pricing.coaching.desc':
    "Une séance d'1h encadrée par un coach certifié, organisée avec toi. À réserver au moins 48h à l'avance.",
  'home.pricing.coaching.unit': 'la séance',
  'home.pricing.coaching.cta': 'Réserver un coaching',
  'home.pricing.footnote':
    "Annulation gratuite jusqu'à 24h avant. Sans engagement, sans abonnement.",

  'home.packs.eyebrow': 'Packs',
  'home.packs.title': "Packs d'heures",
  'home.packs.lead': "Achète plusieurs heures d'un coup et économise sur le tarif à l'unité.",

  'home.door.eyebrow': 'Accès au studio',
  'home.door.title': 'Ouvrir et fermer la porte',
  'home.door.lead':
    'Une courte vidéo te montre comment entrer avec ton code PIN et bien refermer le studio en partant.',

  'home.contact.eyebrow': 'Contact',
  'home.contact.title': 'Contact',
  'home.contact.emailLabel': 'Email : ',
  'home.contact.address': 'Adresse : 1 bis Rue Riboutté, 75009 Paris',
  'home.contact.cta': 'Réserver une séance',

  // Carte de pack (plan 95-07). Préfixe `pack.*` et NON `home.*` : la même
  // carte est montée par la page d'accueil, la page Packs et le Dashboard
  // (plan 95-08). La ranger sous `home.` la ferait suivre le mauvais chunk au
  // découpage de la Phase 97.
  //
  // Le titre et le sous-titre sont des GABARITS interpolés : la carte ne
  // connaît que des centimes et un pourcentage, jamais un « € » écrit à la
  // main, sans quoi l'anglais afficherait « 220 € » au lieu de « €220 ».
  'pack.eyebrow': 'PACK',
  'pack.title': '{hours} h — {price}',
  'pack.subtitle': '{hourly} / h (−{discount} %)',
  'pack.terms':
    'Valable 1 an après achat · Aucun refund après 1ʳᵉ séance consommée (rétractation 14 j si aucune séance).',
  'pack.buy': 'Acheter ce pack',
  'pack.redirecting': 'Redirection…',

  // Section « Comment venir » + vidéo d'accès (plan 95-07). Les attributs sont
  // du texte comme le reste : `aria-label` de la carte, `title` de l'iframe.
  // Un attribut resté français sur une page anglaise est une régression
  // d'accessibilité que ni l'œil ni un test de texte visible ne voient
  // (T-95-14).
  'access.eyebrow': 'Accès',
  'access.title': 'Comment venir',
  'access.map.aria': 'Carte Google Maps — accès au studio SweatStudio',
  'access.map.title': "Carte d'accès au studio",
  'access.metro.title': 'Métro',
  'access.metro.cadet': 'Cadet (ligne 7) — 2 min',
  'access.metro.grandsBoulevards': 'Grands Boulevards (lignes 8, 9) — 5 min',
  'access.metro.notreDame': 'Notre-Dame-de-Lorette (ligne 12) — 7 min',
  'access.bus.title': 'Bus',
  'access.bus.montholon': 'Square Montholon (1 min) — lignes 26, 32, 43, 45, 74, 85',
  'access.bus.cadet': 'Cadet — ligne 56',
  'access.train.title': 'Train / RER',
  'access.train.nord': 'Gare du Nord (RER B, D · Transilien H) — 10 min à pied',
  'access.train.saintLazare': 'Gare Saint-Lazare (Transilien J, L) — 1 arrêt de métro',
  'access.velib.title': "Vélib'",
  'access.velib.station': 'Station Square Montholon n°9009',
  'access.velib.walk': '26 rue de Montholon, 1 min à pied',
  'access.velib.link': 'Voir la disponibilité en temps réel',
  // Deux paires `_one` / `_other` résolues par `pluralKey` (plan 95-09). C'est
  // le seul compteur du produit qui atteint VRAIMENT zéro (une station vide) :
  // le français y reste au singulier (« 0 vélo dispo ») là où l'anglais passe
  // au pluriel (« 0 bikes available »). Un « s » cousu à la main ne sait pas ça.
  'access.velib.bikes_one': '{count} vélo dispo ({ebike} élec · {mechanical} méca)',
  'access.velib.bikes_other': '{count} vélos dispo ({ebike} élec · {mechanical} méca)',
  'access.velib.docks_one': '{count} place libre',
  'access.velib.docks_other': '{count} places libres',
  'access.velib.live': 'en direct',
  'access.parking.title': 'Parking',
  'access.parking.name': 'Parking Bellefond',
  'access.parking.walk': '10 rue de Bellefond, 3 min à pied',
  'access.video.title': 'Ouvrir et fermer la porte du studio',
  'access.video.placeholder': 'Vidéo explicative bientôt disponible',

  // Photos du studio (plan 95-07). Les textes alternatifs sont destinés aux
  // lecteurs d'écran : ils se traduisent comme le texte visible. Le contrat
  // VIS-04 (≤125 caractères, pas de « Photo de », pas de nom propre de
  // personne) porte sur ces valeurs — StudioGallery.ALT_TEXT les relit ici,
  // il n'en existe pas de seconde copie à faire dériver.
  'studio.gallery.aria': 'Galerie du studio',
  'studio.lightbox.close': 'Fermer la photo agrandie',
  'studio.zoom': 'Agrandir la photo : {alt}',
  'studio.hero.alt':
    "Intérieur du studio SweatStudio — vue d'ensemble de l'espace d'entraînement privé",
  'studio.alt.01':
    "Vue d'ensemble du studio depuis le fond, SkiErg et anneaux, porte rouge sur rue",
  'studio.alt.02':
    "Vue d'ensemble depuis le fond : rack mural, anneaux, sangles de suspension et rameur, porte rouge sur la rue",
  'studio.alt.03':
    "Le studio sur toute sa longueur : ski ergomètre, disques, box pliométrique, haltères et kettlebells",
  'studio.alt.04':
    "Coin matériel : disques olympiques 5, 10 et 15 kg sur corbeille murale, banc et box pliométrique",
  'studio.alt.05':
    "Portique mural, anneaux et sangles de suspension, miroir pleine hauteur, minuteur et enceinte",
  'studio.alt.06':
    "Montant de rack et ses bras de sécurité rembourrés, tableau blanc, radiateur",
  'studio.alt.07':
    "Ski ergomètre Concept2 au mur, barre olympique et barre EZ sur support, box pliométrique",
  'studio.alt.08':
    "Rack d'haltères hexagonales, rangée de kettlebells, gants en niche murale, ventilateur sur pied",
  'studio.alt.09':
    "Disques olympiques 5, 10 et 15 kg sur corbeille murale, medicine balls au sol",
  'studio.alt.10': 'Douche carrelage orangé motif rétro, rideau noir, pomme de douche fixe',

  // Tableau de bord (plan 95-08) — la surface CONNECTÉE, celle qui rend
  // I18N-03 observable : nulle part ailleurs une session et du texte traduit
  // ne coexistent.
  //
  // Le message d'annulation reprend mot pour mot celui de la page d'accueil
  // mais garde SA clé : ranger le tableau de bord sous `home.` le ferait
  // suivre le chunk de la vitrine au découpage de la Phase 97, alors qu'aucun
  // visiteur anonyme ne voit jamais cette page. Même raison que `pack.*`.
  'dashboard.cancel.message': 'Paiement annulé. Tu peux réessayer quand tu le souhaites.',
  'dashboard.cancel.close': 'Fermer la notification',

  // Messages destinés au visiteur, choisis par le paramètre `?error=` de
  // l'URL. Les CODES eux-mêmes (`trial_token_expired`, `missing_hold`,
  // `invalid_duration`) restent en anglais et restent comparés à une liste
  // blanche AVANT de choisir une clé : une valeur d'URL n'est jamais une clé
  // de catalogue (T-95-15).
  'dashboard.error.trialTokenExpired':
    "Lien d'essai expiré (15 min). Recommence depuis ton espace.",
  'dashboard.error.missingHold': 'Lien de réservation invalide. Recommence depuis ton espace.',
  'dashboard.error.invalidDuration': 'Durée de séance invalide.',

  'dashboard.buyHours.title': 'Acheter des heures',

  'dashboard.premiere.eyebrow': 'OFFRE DÉCOUVERTE',
  'dashboard.premiere.title': 'Première séance — {price}',
  'dashboard.premiere.desc': 'Une seule fois — pour découvrir le studio.',
  'dashboard.premiere.cta': 'Réserver ma première séance',

  'dashboard.coaching.eyebrow': 'AVEC LE COACH',
  'dashboard.coaching.title': 'Coaching personnalisé — {price}',
  'dashboard.coaching.desc':
    "Une séance d'1h encadrée par un coach certifié, à réserver au moins 48h à l'avance.",
  'dashboard.coaching.cta': 'Réserver un coaching',

  // Le tarif horaire est un prix formaté PUIS habillé d'une unité traduite,
  // jamais `prix + '/h'` : ni le symbole ni la position de l'unité ne
  // survivent au changement de langue (« 25 €/h » → « €25/hr »).
  'dashboard.unit.eyebrow': "À L'UNITÉ",
  'dashboard.unit.perHour': '{price}/h',
  'dashboard.unit.title': "Séance à l'unité — {rate}",
  'dashboard.unit.desc': 'Tarif horaire plein',
  'dashboard.unit.terms': 'Paiement en ligne par carte · Aucun pack requis.',
  'dashboard.unit.cta': 'Réserver au tarif horaire',

  // Bandeau de complétion de profil (plan 95-08). `profile.title` sert deux
  // fois : le libellé visible ET l'aria-label de la région. Un seul texte à
  // l'écran comme au lecteur d'écran, donc une seule clé.
  'profile.title': 'Complète ton profil',
  'profile.desc':
    "Ajoute ton numéro pour qu'on puisse te contacter simplement en cas de problème, et plus tard recevoir tes codes directement sur WhatsApp.",
  'profile.cta': 'Compléter mon profil',
  'profile.dismiss': 'Plus tard',

  // Liste des séances du tableau de bord (plan 95-09) — le corps réel de la
  // page connectée. Valeurs françaises recopiées à l'octet du JSX d'origine :
  // les trois suites `DashboardSessionsSection*` épinglent ces chaînes et n'ont
  // pas été touchées, un rouge chez elles voudrait dire une recopie de travers.
  //
  // Les paires `_one` / `_other` sont résolues par `pluralKey` : c'est ICU qui
  // choisit la forme, jamais un `> 1 ? 's' : ''`. Les six ternaires de suffixe
  // qui vivaient dans ce composant ont disparu.
  'dashboard.sessions.title': 'Mes séances',
  'dashboard.sessions.loading': 'Chargement…',
  'dashboard.sessions.balanceEmpty': 'Solde épuisé —',
  'dashboard.sessions.buyPackLink': 'achète un pack ci-dessous.',
  'dashboard.sessions.reserve': 'Réserver',
  // Solde nul + pré-réservation en cours. La forme singulière ne porte plus le
  // nombre : l'ancienne rendait « Tes 1 séance sont », faute de grammaire que
  // le passage au catalogue corrige. La forme plurielle, elle, est l'originale
  // à l'octet.
  'dashboard.sessions.holdsPending_one':
    'Ta séance est actuellement en cours de réservation. Patiente quelques instants ou finalise une réservation en cours.',
  'dashboard.sessions.holdsPending_other':
    'Tes {count} séances sont actuellement en cours de réservation. Patiente quelques instants ou finalise une réservation en cours.',
  'dashboard.sessions.packExpires':
    'Ton pack expire le {date}. Réserve tes séances avant cette date.',
  'dashboard.sessions.available_one': '{count} séance disponible',
  'dashboard.sessions.available_other': '{count} séances disponibles',
  'dashboard.sessions.validUntil': "Valables jusqu'au {date}",
  'dashboard.sessions.creditsHeld_one': '{count} crédit réservé (en attente de confirmation)',
  'dashboard.sessions.creditsHeld_other': '{count} crédits réservés (en attente de confirmation)',
  'dashboard.sessions.pickDuration': 'Sélectionne une durée',
  'dashboard.sessions.durationAria': 'Durée de séance',
  'dashboard.sessions.reserveHours_one': 'Réserver {count} heure',
  'dashboard.sessions.reserveHours_other': 'Réserver {count} heures',

  // Ligne de séance : type, statut, actions. Les `aria-label` sont des GABARITS
  // interpolés — jamais une concaténation de fragments, l'ordre d'une phrase ne
  // survit pas au changement de langue.
  'dashboard.sessions.type.cancelledRefunded': 'Annulée (remboursée)',
  'dashboard.sessions.type.cancelledNoRefund': 'Annulée (non remboursée)',
  'dashboard.sessions.type.session': 'Séance',
  'dashboard.sessions.type.coaching': 'Coaching privé',
  'dashboard.sessions.type.prepaid': 'Prépayée',
  'dashboard.sessions.type.trial': 'Essai',
  'dashboard.sessions.type.paid': 'Payante',
  'dashboard.sessions.status.cancelled': 'Annulée',
  'dashboard.sessions.status.running': 'En cours',
  'dashboard.sessions.status.upcoming': 'À venir',
  'dashboard.sessions.status.past': 'Passée',
  'dashboard.sessions.cancel': 'Annuler',
  'dashboard.sessions.cancelAria': 'Annuler la séance du {date}',
  'dashboard.sessions.review': 'Donner mon avis',
  'dashboard.sessions.reviewAria': 'Donner mon avis sur la séance du {date}',
  'dashboard.sessions.reviewPrompt': "Comment s'est passée votre séance du {date} ?",
  'dashboard.sessions.reviewPromptCta': 'Noter ma séance',
  'dashboard.sessions.replyLabel': 'Réponse du studio',
  'dashboard.sessions.pin': 'Code PIN porte :',

  // Pré-réservations (lignes « held »). Les messages sont ceux du sélecteur de
  // créneaux mot pour mot, mais gardent LEUR clé : ranger le tableau de bord
  // sous `reserve.` le ferait suivre le mauvais chunk à la Phase 97.
  'dashboard.sessions.hold.label': 'Pré-réservation',
  'dashboard.sessions.hold.pending': 'En attente · expire {time}',
  'dashboard.sessions.hold.resume': 'Reprendre',
  'dashboard.sessions.hold.resumeAria': 'Reprendre la pré-réservation du {date}',
  'dashboard.sessions.hold.release': 'Libérer',
  'dashboard.sessions.hold.releaseAria': 'Libérer la pré-réservation du {date}',
  'dashboard.sessions.hold.released':
    'Pré-réservation libérée — le créneau est de nouveau disponible.',
  'dashboard.sessions.hold.noLongerPending':
    "Cette pré-réservation n'est plus en attente — elle a déjà été confirmée ou libérée.",
  'dashboard.sessions.hold.releaseFailed':
    'Impossible de libérer la pré-réservation. Réessaie plus tard.',
  'dashboard.sessions.hold.notYours':
    "Cette pré-réservation ne t'appartient pas — elle a été ouverte depuis un autre compte.",
  'dashboard.sessions.hold.expiredPickNew':
    'Ta pré-réservation a expiré — choisis un nouveau créneau.',

  'dashboard.sessions.review.notFinished':
    "Cette séance n'est pas encore terminée — l'avis sera possible juste après.",
  'dashboard.sessions.review.failed': "Impossible d'ouvrir le formulaire d'avis pour le moment.",

  'dashboard.sessions.empty': 'Aucune séance pour le moment.',
  'dashboard.sessions.noUpcoming': 'Aucune séance à venir.',
  'dashboard.sessions.filter.showPast': 'Afficher les séances passées',
  'dashboard.sessions.filter.hidePast': 'Masquer les séances passées',
  'dashboard.sessions.filter.showCancelled': 'Afficher les séances annulées',
  'dashboard.sessions.filter.hideCancelled': 'Masquer les séances annulées',

  // Dialogue d'annulation. L'avertissement tardif garde ses <strong> en JSX
  // (T-95-06 : aucun dangerouslySetInnerHTML) — d'où trois fragments autour de
  // l'emphase, chaque langue écrivant les siens, comme le titre du hero.
  'dashboard.sessions.cancelDialog.refundable':
    'Annuler cette réservation ? Si elle a été payée avec un crédit pack, le crédit sera restitué. Si elle a été payée directement par carte, le remboursement Stripe sera émis.',
  'dashboard.sessions.cancelDialog.lateTitle': 'Annulation tardive ({delay} avant la séance).',
  'dashboard.sessions.cancelDialog.lateDelayUnderHour': "moins d'1 heure",
  'dashboard.sessions.cancelDialog.policyLead':
    'Notre politique prévoit un remboursement uniquement pour les annulations effectuées au moins 24h avant la séance. Si tu annules maintenant, ton crédit pack',
  'dashboard.sessions.cancelDialog.policyStrong': 'ne sera pas restitué',
  'dashboard.sessions.cancelDialog.policyTail':
    'et aucun remboursement Stripe ne sera émis. Le créneau sera libéré pour un autre utilisateur.',
  'dashboard.sessions.cancelDialog.contactLead':
    "Pour un remboursement exceptionnel (urgence, erreur, etc.), contacte l'admin via",
  'dashboard.sessions.cancelDialog.contactTail': "avant d'annuler.",
  'dashboard.sessions.cancelDialog.confirming': 'Annulation…',
  'dashboard.sessions.cancelDialog.confirm': "Confirmer l'annulation",
  'dashboard.sessions.cancelDialog.confirmNoRefund': 'Annuler sans remboursement',
  'dashboard.sessions.cancelDialog.keep': 'Garder ma réservation',

  // Messages d'annulation choisis par un code serveur. Les CODES restent en
  // anglais et passent par une liste blanche explicite AVANT de choisir une
  // clé : une valeur venue de la base n'est jamais une clé de catalogue
  // (T-95-15).
  'dashboard.sessions.cancelError.generic': 'Annulation impossible. Réessaie plus tard.',
  'dashboard.sessions.cancelError.notOwner': "Tu n'es pas propriétaire de cette réservation.",
  'dashboard.sessions.cancelError.pastSlot': "Impossible d'annuler une séance passée.",
  'dashboard.sessions.cancelError.status': 'Cette séance ne peut plus être annulée.',
  'dashboard.sessions.cancelError.notFound': 'Réservation introuvable.',
  'dashboard.sessions.cancelError.stripeRefund':
    'Le remboursement Stripe a échoué. Réessaie dans un instant.',

  // Quatre messages de succès ENTIERS plutôt qu'un tronc et des morceaux
  // recollés : la parenthèse ne se place pas au même endroit d'une langue à
  // l'autre, et une phrase assemblée par concaténation ne se réordonne pas.
  'dashboard.sessions.cancelDone': 'Réservation annulée.',
  'dashboard.sessions.cancelDonePack': 'Réservation annulée (crédit pack restitué).',
  'dashboard.sessions.cancelDoneStripe': 'Réservation annulée (remboursement Stripe émis).',
  'dashboard.sessions.cancelDoneBoth':
    'Réservation annulée (crédit pack restitué · remboursement Stripe émis).',

  // Page Packs (plan 97-03). Le TITRE n'est pas ici : il réutilise
  // `home.packs.title`, que la section packs de la page d'accueil porte déjà à
  // l'identique. Le chapô, lui, a sa clé bien qu'il soit identique dans les
  // deux langues — le catalogue est la seule source de texte de la page, et le
  // contrôle 2 de `routes.test.ts` exige que la page appelle `t(...)`.
  //
  // Le sous-titre est PROCHE de `home.packs.lead` sans lui être identique
  // (vouvoiement ici, tutoiement sur l'accueil) : deux chaînes distinctes,
  // deux clés. Valeur française recopiée à l'octet du JSX d'origine.
  'packs.eyebrow': 'Packs',
  'packs.lead': "Achetez plusieurs heures d'un coup et économisez sur le tarif à l'unité.",

  // Page de connexion (plan 97-03). Deux états — « saisie » et « lien envoyé »
  // — qui partagent le chapô et le message d'adresse invalide : UNE clé chacun,
  // pas deux copies à faire diverger.
  //
  // `login.sent.body` est UNE clé interpolée, jamais deux fragments recollés.
  // L'adresse est stylée par un <span> AU MILIEU de la phrase, et chaque langue
  // place ce milieu où elle veut — même raison que les quatre messages
  // d'annulation du tableau de bord. La page découpe la valeur traduite sur son
  // propre marqueur `{email}` ; une concaténation maison figerait l'ordre
  // français, et le harnais D-04 (qui compare les valeurs interpolées par
  // gabarit) la signalerait comme fuite.
  //
  // Le compteur de renvoi apparaît DEUX fois dans le JSX : une seule clé.
  //
  // T-97-07 — les trois messages d'erreur restent génériques et ne distinguent
  // pas « compte inexistant » de « adresse invalide ». L'anglais conserve cette
  // indistinction : l'affiner serait un oracle d'énumération de comptes.
  //
  // Le placeholder est du texte comme le reste : laissé en dur, il serait resté
  // français sur une page anglaise sans qu'aucun scan de texte visible le voie.
  'login.eyebrow': 'CONNEXION',
  'login.title': 'Accède à ton espace',
  'login.lead': 'Entre ton email. Nous t’envoyons un lien de connexion, valable 1 heure.',
  'login.emailLabel': 'Email',
  'login.emailPlaceholder': 'toi@exemple.com',
  'login.submit': 'Recevoir le lien',
  'login.sending': 'Envoi en cours…',
  'login.marketing.label': 'Nouveau compte : recevoir les créneaux libérés, nouveautés et offres.',
  'login.marketing.help': 'Désactivable à tout moment dans Mon compte ou via le lien en bas de chaque mail.',
  'login.helper': 'Pas de mot de passe. Pas de compte à créer. Le lien suffit.',
  'login.resend': 'Renvoyer le lien',
  'login.resendIn': 'Renvoyer dans {seconds}s…',
  'login.sent.title': 'Vérifie ta boîte mail',
  'login.sent.body':
    'Un lien de connexion a été envoyé à {email}. Clique sur le lien dans les prochaines 60 minutes pour te connecter.',
  'login.sent.spam': 'Email envoyé. Si pas reçu dans 5 min, vérifie les spams.',
  'login.error.invalidEmail': 'Adresse email invalide.',
  'login.error.rateLimited':
    'Trop de demandes. Patiente environ une minute avant de redemander un lien.',
  'login.error.generic':
    "Impossible d'envoyer le lien pour le moment. Réessaie dans quelques secondes.",

  // Page de choix de créneau (plan 97-04). Le TITRE n'est pas ici : il réutilise
  // `reserve.title`, que la page de réservation porte déjà à l'identique.
  //
  // Le chapô est UNE clé interpolée. Trois autres clés du catalogue portent ses
  // morceaux (`home.booking.eyebrow`, `dashboard.sessions.type.session`, le prix
  // via `formatEuros`) et les recoller dans le JSX aurait figé l'ordre et le
  // séparateur FRANÇAIS dans le code — même raison que les quatre messages
  // d'annulation du tableau de bord.
  //
  // `{price}` vient de `formatEuros`, jamais d'un littéral : c'est ce qui met le
  // symbole du bon côté (« 25 € » contre « €25 ») et l'espace insécable étroit
  // là où le français le veut. Le nombre de décimales se décide PAR MONTANT
  // (`money.ts`), donc le rendu français reste « 25 € » — décision opérateur du
  // 2026-08-16, une phase de traduction ne change pas ce que voient les
  // visiteurs francophones déjà là.
  'sessionCheckout.eyebrow': 'Réservation — Séance unitaire — {price}/h',
  'sessionCheckout.lead':
    'Sélectionne l’horaire qui te convient, puis finalise le paiement Stripe. Code PIN d’accès envoyé par email dès la réservation, valable 15 minutes avant l’heure.',

  // Page de succès du paiement de séance (plan 97-04). Préfixe `sessionSuccess.`
  // et NON `purchaseSuccess.` : `PurchaseSuccess.tsx` (le succès d'achat de
  // PACK, `/purchase/success`) est une autre page du même périmètre, encore à
  // traduire. Lui prendre son préfixe l'aurait forcée à en inventer un pire.
  //
  // HUIT blocs d'état, pas sept : `pending` (« Programmation porte en cours »)
  // s'ajoute aux sept que l'inventaire du plan recense. Chacun a ses clés.
  //
  // Les `aria-label` des deux spinners sont ici comme le reste : `textContent`
  // ne les voit jamais, et seul `attrText` du harnais D-04 les attrape. Laissés
  // en dur, ils seraient restés français sur une page anglaise indéfiniment.
  //
  // Les phrases coupées sur plusieurs lignes du JSX d'origine sont UNE chaîne
  // chacune, recopiée telle qu'elle SE REND (espaces de jointure du JSX
  // compris), pas telle qu'elle était indentée.
  //
  // `sessionSuccess.dashboardCta` est mutualisée : le même bouton « Tableau de
  // bord » apparaît sur cinq états. Cinq copies auraient divergé sans que rien
  // ne le signale. La variante longue de l'état payé (« Voir mon tableau de
  // bord ») est une AUTRE chaîne, donc une autre clé.
  //
  // `sessionSuccess.paid.homeCta` ne réutilise PAS `notFound.home`, qui vaut
  // "Retour à l'accueil" avec une apostrophe DROITE là où cette page porte la
  // typographique. Mutualiser aurait changé le rendu français de l'une des deux
  // pages — même arbitrage que `packs.lead` en 97-03.
  //
  // T-97-08 — le code PIN, l'heure du créneau et l'identifiant Stripe sont des
  // DONNÉES : aucune valeur de ce catalogue n'en porte, aucune ne doit en
  // porter.
  'sessionSuccess.loading.aria': 'Vérification du paiement',
  'sessionSuccess.loading.title': 'Vérification du paiement…',
  'sessionSuccess.loading.body':
    'Nous récupérons ton code PIN. Cela prend généralement quelques centaines de millisecondes.',

  'sessionSuccess.pending.aria': 'Programmation porte en cours',
  'sessionSuccess.pending.title': 'Programmation porte en cours…',
  'sessionSuccess.pending.body':
    'Ton paiement est confirmé. Nous finalisons la programmation de la porte.',

  'sessionSuccess.paid.eyebrow': 'Paiement confirmé',
  'sessionSuccess.paid.title': 'Réservation confirmée',
  'sessionSuccess.paid.pinLabel': 'Code PIN porte',
  'sessionSuccess.paid.pinHint':
    'Le code devient valable 15 minutes avant l’heure de ta séance.',
  'sessionSuccess.paid.accessLabel': 'Accès au studio',
  'sessionSuccess.paid.accessHint': 'Comment ouvrir et fermer la porte avec ton code PIN.',
  'sessionSuccess.paid.switchbotWarning':
    'Programmation porte en cours. Si elle n’est pas active 15 minutes avant le créneau, contacte le studio.',
  'sessionSuccess.paid.emailNote':
    'Ton code PIN d’accès au studio t’a également été envoyé par email.',
  'sessionSuccess.paid.dashboardCta': 'Voir mon tableau de bord',
  'sessionSuccess.paid.homeCta': 'Retour à l’accueil',

  'sessionSuccess.notPaid.title': 'Paiement non détecté',
  'sessionSuccess.notPaid.body':
    'Nous n’avons pas trouvé de paiement validé pour cette session. Si tu viens de régler, patiente quelques secondes puis rafraîchis la page. Sinon, retente la réservation depuis ton tableau de bord.',
  'sessionSuccess.notPaid.pickSlotCta': 'Choisir un créneau',

  'sessionSuccess.notFound.title': 'Session inconnue',
  'sessionSuccess.notFound.body':
    'Ce lien de paiement n’est pas reconnu. Contacte le studio si tu penses qu’il s’agit d’une erreur, ou reviens à ton tableau de bord.',

  'sessionSuccess.expired.title': 'Confirmation déjà finalisée',
  'sessionSuccess.expired.body':
    'Ce lien de confirmation est trop ancien pour afficher le code PIN. Consulte ton email pour récupérer ton code, ou retrouve la séance depuis ton tableau de bord.',

  'sessionSuccess.timeout.title': 'Confirmation différée',
  'sessionSuccess.timeout.body':
    'La confirmation prend plus longtemps que prévu. Pas d’inquiétude — ton paiement est bien enregistré et ta séance sera réservée.',
  'sessionSuccess.timeout.emailNote':
    'Consulte ton email pour récupérer ton code PIN, ou rends-toi sur ton tableau de bord d’ici quelques minutes.',

  'sessionSuccess.missing.title': 'Lien de confirmation incomplet',
  'sessionSuccess.missing.body':
    'Cette page a été ouverte sans identifiant de session Stripe. Si tu viens de payer, consulte ton tableau de bord pour voir ta séance.',

  'sessionSuccess.dashboardCta': 'Tableau de bord',

  // Blog — la COQUILLE seulement (Phase 97, plan 97-05). Le titre des articles
  // et leur `content_html` sont des DONNÉES éditoriales : depuis la Phase 100
  // elles ont deux versions EN BASE (`title_en`, `content_html_en`), jamais dans
  // ce catalogue. Aucune clé de ce bloc n'en porte, aucune ne doit en porter.
  //
  // Le chapô « Blog » RÉUTILISE `nav.blog` — même mot, mêmes deux langues. Une
  // clé jumelle passerait la parité du catalogue sans broncher et ferait
  // diverger les deux à la première retouche. `Chargement…` réutilise de même
  // `dashboard.sessions.loading` dans les deux pages du blog.
  'blog.title': 'Articles',
  'blog.error': 'Une erreur est survenue au chargement des articles.',
  'blog.empty': 'Aucun article pour le moment. Revenez bientôt !',

  // Repli ANGLAIS des pages du blog. Les deux valeurs sont en anglais DANS LES
  // DEUX catalogues, comme `enBanner.*` : elles ne s'affichent que sous /en/, où
  // un texte français accentué serait précisément la fuite que le harnais D-04
  // traque. Deux valeurs identiques sortent aussi du périmètre de
  // `catalogueLeaks` (qui saute les clés dont fr === en), et c'est exact : ces
  // chaînes ne peuvent pas fuir, elles SONT la version anglaise.
  //
  // ⚠ Les deux valeurs doivent rester IDENTIQUES AU CARACTÈRE PRÈS entre fr.ts
  // et en.ts. `Blog.render.test.tsx` compare le texte rendu à la valeur ANGLAISE,
  // et une divergence d'un seul espace ferait de plus entrer ces bandeaux dans le
  // champ de `catalogueLeaks`. Copier-coller d'un fichier à l'autre, ne pas
  // retaper.
  //
  // Depuis la Phase 100, `blog_posts` porte `title_en` / `content_html_en` et un
  // drapeau généré `has_en_translation` : /en/blog liste les articles réellement
  // traduits, et le bandeau dit ce qui reste hors de cette liste. Un article non
  // traduit ouvert sous /en/ se sert en français, précédé de son propre
  // avertissement (`article.enUnavailable`).
  'blog.enUnavailable':
    'Only articles available in English are listed here. The full blog is published in French.',

  'article.error': "Une erreur est survenue au chargement de l'article.",
  'article.notFound.title': 'Article introuvable',
  'article.notFound.body': "Cet article n'existe pas ou n'est plus publié.",
  'article.enUnavailable':
    'This article is not available in English yet — the French version is shown below.',

  // Succès d'achat de PACK (Phase 97, plan 97-06). Préfixe `purchase.` — le
  // préfixe `sessionSuccess.` appartient à /session/purchase/success, l'autre
  // page de succès, et 97-04 l'a séparé exprès.
  //
  // Le titre RÉUTILISE `sessionSuccess.paid.eyebrow` : valeur identique à
  // l'octet dans les DEUX langues, et deux clés jumelles finiraient par
  // diverger sans que rien ne le signale (même arbitrage que
  // `dashboard.sessions.loading` réutilisée par le blog en 97-05).
  //
  // Le rappel spam RÉUTILISE `login.sent.spam`, posé par 97-03 : c'est le même
  // texte au mot près sur /login et ici. Son préfixe `login.` décrit sa
  // PREMIÈRE page, pas sa seule.
  //
  // DEUX compteurs passent par `pluralKey` (plan 95-09) — le solde et la taille
  // du pack. Le JSX cousait le pluriel à la main (`{n} heures` en dur, ternaire
  // pour la taille du pack) et rendait donc « 1 heures ». En anglais le défaut
  // serait « 1 hours », visible du premier coup d'œil. La branche « pack sans
  // taille » n'est PAS une forme plurielle : c'est une troisième phrase, donc
  // sa propre clé.
  //
  // `purchase.creditingAria` est le SEUL endroit par lequel cette page fuyait :
  // un `aria-label`, que `textContent` ne voit jamais.
  //
  // `purchase.expiry` ne porte PAS le séparateur « · » qui la précède : c'est
  // de la ponctuation de mise en page, elle reste dans le JSX.
  'purchase.eyebrow': 'PAIEMENT',
  'purchase.balanceLabel': 'Solde :',
  'purchase.balanceHours_one': '{count} heure',
  'purchase.balanceHours_other': '{count} heures',
  'purchase.expiry': "Valable jusqu'au {date}.",
  'purchase.dashboardCta': 'Aller à mon dashboard',
  'purchase.creditingAria': 'Crédit en cours',
  'purchase.crediting_one': 'Ton pack de {count} heure est en cours de crédit.',
  'purchase.crediting_other': 'Ton pack de {count} heures est en cours de crédit.',
  'purchase.creditingNoSize': 'Ton pack est en cours de crédit.',
  'purchase.refresh': 'Actualiser',
  'purchase.slow60':
    'Le paiement est en cours de traitement. Tu recevras un email de confirmation sous peu.',
  'purchase.refreshError': 'Impossible de rafraîchir. Réessaie ou consulte ton email.',

  // Retour de lien magique (Phase 97, plan 97-06). Trois états — connexion en
  // cours, lien expiré, lien invalide — dont deux portent la même carte et le
  // même bouton de retour.
  //
  // Le chapô RÉUTILISE `login.eyebrow` : c'est le même mot, sur le même
  // parcours, et deux clés jumelles finiraient par diverger. `Retour à la
  // connexion` apparaît DEUX fois dans le JSX et porte UNE clé.
  //
  // T-97-15 — les deux messages restent aussi génériques qu'aujourd'hui : ils
  // ne distinguent pas un jeton expiré d'un jeton falsifié plus finement que le
  // français. Ne pas affiner en anglais, ce serait un oracle.
  //
  // Ton RASSURANT en anglais : un lien expiré n'est pas une faute de
  // l'utilisateur, la phrase dit quoi faire plutôt que ce qui a échoué.
  'authCallback.loading': 'Connexion en cours…',
  'authCallback.expired.title': 'Ce lien a expiré',
  'authCallback.expired.body':
    'Les liens de connexion sont valables 1 heure. Demandez-en un nouveau.',
  'authCallback.invalid.title': 'Lien invalide',
  'authCallback.invalid.body':
    "Ce lien n'est pas valide. Demandez un nouveau lien de connexion.",
  'authCallback.backToLogin': 'Retour à la connexion',

  // Guide d'installation PWA (Phase 97, plan 97-06). Cinquième et DERNIÈRE page
  // indexable du périmètre.
  //
  // Les quatre étapes iOS étaient un tableau de CHAÎNES au milieu du JSX. Elles
  // deviennent un tableau de CLÉS (`MsgKey`) au niveau module, motif déjà posé
  // par `navLinks` dans `Header.tsx` : `t` n'existe pas hors du composant, seule
  // la clé peut voyager.
  //
  // `install.android.fallback` porte le glyphe ⋮ (U+22EE) EN CLAIR et non
  // l'entité `&#8942;` du JSX d'origine. Une entité dans une valeur de catalogue
  // se rendrait littéralement (`&#8942;`), React échappant le texte qu'il
  // insère : c'est le glyphe qui préserve le rendu, pas l'entité.
  //
  // Les guillemets « » du français restent exacts ; l'anglais prend des
  // guillemets droits, c'est de la typographie et non de la traduction.
  //
  // L'`aria-label` de la liste iOS est du texte comme le reste : laissé en dur,
  // il serait resté français pour les lecteurs d'écran anglophones sans qu'aucun
  // scan de texte visible ne le voie.
  'install.title': "Installe SweatStudio sur ton écran d'accueil",
  'install.platform.ios': 'Sur iOS Safari',
  'install.platform.android': 'Sur Android Chrome',
  'install.platform.other': 'Ton navigateur',
  'install.ios.aria': "Étapes d'installation sur iOS",
  'install.ios.step1': 'Appuie sur le bouton Partager',
  // Les espaces insécables ( ) des étapes iOS sont RECOPIÉES du JSX
  // d'origine, où elles sont invisibles à la relecture. Les remplacer par des
  // espaces ordinaires changerait le rendu français.
  'install.ios.step2':
    "Fais défiler vers le bas et appuie sur « Ajouter à l'écran d'accueil »",
  'install.ios.step3': 'Confirme en appuyant sur « Ajouter »',
  'install.ios.step4':
    "Ouvre SweatStudio depuis ton écran d'accueil — tu es en mode application !",
  'install.android.cta': 'Installer SweatStudio',
  'install.android.fallback': "Appuie sur le menu ⋮ de Chrome → « Ajouter à l'écran d'accueil »",
  'install.qr.hint': 'Scanne ce code avec ton téléphone pour installer SweatStudio.',
  'install.push.note':
    'Après installation, tu pourras activer les notifications push pour recevoir des rappels la veille de tes séances.',
  // Profil utilisateur (Phase 97, plan 97-07). Page à COMPORTEMENT : deux
  // toasts, une aide de champ et une erreur de saisie vivent hors de l'état par
  // défaut, donc hors de ce que le harnais D-04 scanne — d'où le fichier
  // `MonCompte.i18n.test.tsx` qui les monte un par un.
  //
  // `Chargement…` n'a PAS sa clé ici : elle RÉUTILISE
  // `dashboard.sessions.loading`, même mot pour le même geste (motif déjà
  // employé par les deux pages du blog en 97-05).
  //
  // Le chapô est stocké en CAPITALES comme `login.eyebrow` et `purchase.eyebrow`
  // — le `uppercase` du JSX ne fait que le confirmer visuellement, mais c'est la
  // valeur qui doit rester la copie exacte de la chaîne d'origine.
  //
  // Les deux `placeholder` sont du texte comme le reste : laissés en dur, ils
  // seraient restés français sur `/en/mon-compte` sans qu'aucun scan de texte
  // visible ne les voie. Celui du téléphone garde la même valeur des deux côtés
  // (l'indicatif par défaut reste +33) — c'est un exemple de format, pas une
  // phrase.
  //
  // Anglais ADAPTÉ (D-03) : l'aide française sur le téléphone est longue et
  // explicative, l'anglaise dit la même chose en moins de mots sans cesser
  // d'être une RAISON — un simple libellé n'expliquerait plus pourquoi on
  // demande le numéro.
  'myAccount.eyebrow': 'MON COMPTE',
  'myAccount.title': 'Profil',
  'myAccount.intro': 'Tes informations sont utilisées pour préremplir la réservation de séance.',
  'myAccount.nameLabel': 'Nom complet',
  'myAccount.namePlaceholder': 'Marie Dupont',
  'myAccount.phoneLabel': 'Téléphone',
  'myAccount.dialCodeAria': 'Indicatif pays',
  'myAccount.phonePlaceholder': '6 12 34 56 78',
  'myAccount.phoneHelp':
    "Pour qu'on puisse te contacter simplement en cas de problème, et plus tard recevoir tes codes directement sur WhatsApp.",
  'myAccount.emailLabel': 'Email',
  'myAccount.emailHelp': 'Synchronisé avec ton compte de paiement. Contacte-nous pour le modifier.',
  'myAccount.save': 'Enregistrer',
  'myAccount.saving': 'Enregistrement…',
  'myAccount.toastSuccess': 'Profil enregistré.',
  'myAccount.marketingTitle': 'Offres et nouveautés',
  'myAccount.marketingLabel': 'Recevoir par email les créneaux libérés, nouveautés et offres.',
  'myAccount.marketingHelp': 'Décoché, tu ne reçois plus que les mails liés à tes réservations (confirmation, code PIN).',
  'myAccount.toastError': 'Enregistrement impossible. Réessaie.',

  // Erreur de saisie du téléphone, partagée par `/mon-compte` et le SlotPicker.
  // `lib/phone.ts` rend désormais cette CLÉ et non le libellé : le libellé y
  // était cousu en français et se rendait tel quel sur les DEUX pages une fois
  // traduites. Aucun scan ne l'attrapait — ni celui de la Phase 96 (par fichier,
  // et le fichier fautif n'est pas la page), ni le harnais D-04 (le champ est
  // vide à l'état par défaut, l'erreur n'existe pas).
  'phone.tooShort': 'Numéro trop court.',
  // Questionnaire d'avis post-séance (Phase 97, plan 97-07). Page à
  // COMPORTEMENT : trois états, et un libellé de champ qui dépend de la NOTE.
  //
  // Les six clés `feedback.prompt.*` remplacent un objet littéral au niveau
  // module qui portait les libellés eux-mêmes. `t` n'existe pas hors du
  // composant : seule la CLÉ peut voyager. Motif déjà posé par `navLinks` dans
  // `Header.tsx` et par `IOS_STEPS` dans `Install.tsx`, et le typage `MsgKey`
  // fait attraper une faute de frappe par `make typecheck-webapp`.
  //
  // Les deux `feedback.thanks.*Body` sont DEUX clés distinctes, pas un tronc et
  // sa variante : l'une renvoie vers un avis Google, l'autre non.
  //
  // T-97-16 — `feedback.tokenError` reste UNE seule phrase pour les trois
  // échecs de jeton (inconnu, expiré, déjà utilisé). L'affiner en anglais en
  // ferait un oracle d'énumération, exactement ce que la RPC serveur évite en
  // faisant passer les trois par un seul UPDATE conditionnel.
  //
  // Les deux `&nbsp;` du JSX deviennent le GLYPHE U+00A0 : React échappe le
  // texte qu'il insère, l'entité s'afficherait littéralement (leçon 97-06).
  // C'est de la typographie française — l'anglais ne met pas d'espace avant
  // « ? » ni « ! ».
  //
  // Anglais CHALEUREUX et non calqué (D-03) : c'est un questionnaire de
  // satisfaction, pas un formulaire administratif.
  'feedback.title': 'Alors, cette première séance ?',
  'feedback.intro': 'Ton avis nous aide à améliorer le studio. Ça prend moins d’une minute.',
  'feedback.ratingAria': 'Note de 1 à 5',
  'feedback.ratingStar': '{n} sur 5',
  'feedback.prompt.neutral.label': 'Alors, comment s’est passée ta séance ?',
  'feedback.prompt.neutral.placeholder': 'Facultatif — mais même trois mots nous aident.',
  'feedback.prompt.high.label': 'Qu’est-ce qui t’a plu ?',
  'feedback.prompt.high.placeholder': 'Ce qu’on peut garder, ou améliorer…',
  'feedback.prompt.low.label': 'Qu’est-ce qui n’a pas été ?',
  'feedback.prompt.low.placeholder': 'Un souci matériel, l’accès, la propreté… dis-nous tout.',
  'feedback.submit': 'Envoyer',
  'feedback.sending': 'Envoi…',
  'feedback.tokenError': 'Ce lien n’est plus valable. Contacte le studio si besoin.',
  'feedback.thanks.title': 'Merci !',
  'feedback.thanks.googleBody':
    'Ravi que la séance t’ait plu. Si tu as une minute de plus, un avis Google aide beaucoup un studio indépendant.',
  'feedback.thanks.noGoogleBody':
    'Ravi que la séance t’ait plu. Ton retour a bien été transmis au studio.',
  'feedback.thanks.googleCta': 'Laisser un avis Google',
  'feedback.internal.title': 'Merci pour ton retour',
  'feedback.internal.body':
    'Ton retour est transmis au studio. Nous le lisons et revenons vers toi si nécessaire.',

  // --- /packs/checkout/:size (97-08) ---------------------------------------
  //
  // Le libellé du champ promo n'a PAS de clé ici : il RÉUTILISE
  // `reserve.promoLabel`, mot pour mot le même dans les deux tunnels. Deux clés
  // jumelles divergeraient à la première retouche.
  // Le repère « SUMMER2026 » reste un littéral du JSX, comme dans SlotPicker :
  // c'est un exemple de code promo, pas de la langue.
  'packCheckout.eyebrow': 'PAIEMENT',
  'packCheckout.intro': 'Pack de {size} h. Renseigne un code promo si tu en as un, puis finalise le paiement.',
  'packCheckout.submit': 'Continuer vers le paiement',
  'packCheckout.redirecting': 'Redirection vers le paiement sécurisé Stripe…',
  'packCheckout.errorTitle': 'Une erreur est survenue',
  // Distincte de `checkoutError.fallback` : celle-ci est le corps d'une page
  // d'erreur pleine, tutoyée comme le reste de la page, et elle ne remplace
  // aucune entrée de la table.
  'packCheckout.errorBody':
    'Impossible de créer la session de paiement pour le moment. Réessaie dans quelques secondes.',
  'packCheckout.backToPacks': 'Retour aux packs',

  // --- Erreurs de paiement (97-08) -----------------------------------------
  //
  // `lib/checkoutError.ts` était une table `code → copie française` : de la
  // COPIE cousue dans un module pur, donc intraduisible. Elle rend désormais des
  // CLÉS, et ces 20 valeurs sont la seule source de texte.
  //
  // Les 15 premières sont des copies EXACTES des chaînes qu'elle portait — rien
  // ne bouge à l'écran en français. Les 5 dernières sont les codes que seule la
  // branche `messageOverride` couvrait (la `message` de l'Edge Function écrasait
  // la copie locale) : leur valeur reprend la chaîne émise par l'EF, pour que la
  // suppression de cette branche ne change rien non plus.
  'checkoutError.fallback': "Impossible d'initier le paiement. Veuillez réessayer.",
  'checkoutError.promo_invalid_for_session': "Ce code promo n'est pas valide pour une séance unitaire.",
  'checkoutError.promo_invalid_for_premiere': "Ce code promo n'est pas valide pour la séance première.",
  'checkoutError.promo_invalid_for_coaching': "Ce code promo n'est pas valide pour le coaching personnalisé.",
  'checkoutError.promo_invalid_for_pack': "Ce code promo n'est pas valide pour un pack.",
  'checkoutError.promo_new_customer_only_violated': 'Ce code promo est réservé aux nouveaux clients.',
  'checkoutError.booking_not_found': 'La réservation a expiré. Réessayez de sélectionner un créneau.',
  'checkoutError.booking_not_held': "Ce créneau n'est plus tenu pour vous. Sélectionnez-en un autre.",
  'checkoutError.hold_expired': 'Votre créneau a expiré. Choisissez-en un autre.',
  'checkoutError.booking_not_owned': "Cette réservation n'appartient pas à votre compte.",
  'checkoutError.invalid_origin': 'Origine invalide. Réessayez depuis sweatstudio.fr.',
  'checkoutError.rate_limited': 'Trop de tentatives. Patientez quelques minutes avant de réessayer.',
  'checkoutError.stripe_error': "Stripe n'a pas pu créer la session de paiement. Réessayez ou contactez le support.",
  'checkoutError.stripe_no_url': 'Erreur Stripe : aucune URL de paiement. Contactez le support.',
  'checkoutError.db_error': 'Erreur interne. Réessayez dans quelques secondes.',
  'checkoutError.internal': "Erreur interne — l'incident a été signalé.",
  // `unauthorized` couvre DEUX messages serveur (« Token manquant. » et « Token
  // invalide. »). Aucun des deux n'est lisible par un client ; la copie dit ce
  // qu'il doit faire, ce qui est vrai dans les deux cas.
  'checkoutError.unauthorized': 'Session expirée. Reconnectez-vous puis réessayez.',
  // Idem pour `bad_input` (« JSON invalide. » et « pack_size invalide… ») : deux
  // messages de développeur, une seule sortie utilisable pour le client.
  'checkoutError.bad_input': 'Requête invalide. Réessayez depuis la page des packs.',
  // ⚠ PLAFOND ASSUMÉ. `create-checkout-session` émet `conflict` pour DEUX
  // situations distinctes (:212 « Code promo invalide pour ce pack. » et :225
  // « Ce code promo est réservé aux nouveaux clients. »). Une clé ne peut pas
  // porter deux phrases : celle-ci est un parapluie, vrai dans les deux cas,
  // et volontairement moins précis que chacune. SORTIE : le jour où
  // `create-checkout-session` émettra `promo_invalid_for_pack` et
  // `promo_new_customer_only_violated` — des codes que la table porte DÉJÀ —
  // la précision revient sans changer une ligne de client. C'est un travail
  // d'Edge Function, hors de la Phase 97 (voir 97-10 T2).
  'checkoutError.conflict': "Ce code promo n'est pas utilisable pour cette commande.",
  // `upstream_error` couvre les trois échecs Stripe côté serveur (prix
  // introuvable, coupon, création de session). Tous appellent le même geste.
  'checkoutError.upstream_error': 'Erreur Stripe — réessayez ou contactez le support.',

  // --- /reserve/trial — l'offre première séance (97-09) ---------------------
  //
  // Le chapô « PREMIÈRE SÉANCE » apparaît DEUX fois dans le fichier (état
  // consommé et état normal) : UNE seule clé. La casse est portée par la classe
  // `uppercase` du JSX, mais la valeur reste en capitales parce que c'est ainsi
  // que le DOM la rend et que le harnais D-04 la lit (motif `packCheckout.eyebrow`).
  //
  // Le titre de l'état consommé RÉUTILISE `home.pricing.trial.title` — même
  // valeur au caractère près, une jumelle aurait divergé.
  //
  // Le libellé de chargement RÉUTILISE `dashboard.sessions.loading` et le CTA
  // `sessionSuccess.paid.dashboardCta` : mêmes chaînes exactes, déjà au
  // catalogue.
  'trial.eyebrow': 'PREMIÈRE SÉANCE',
  'trial.title': 'Ta première séance',
  // Le prix est interpolé AU MILIEU de la phrase et stylé par un `<span>` : la
  // page découpe la valeur sur `{price}` plutôt que de la couper en deux clés
  // `prefix`/`suffix`, dont l'ordre ne se transpose pas d'une langue à l'autre.
  'trial.lead': 'Découvre le studio en autonomie — première séance à {price}.',
  'trial.pending':
    'Tu as une réservation en attente de paiement. Reprendre le paiement ci-dessous pour la finaliser.',
  // La règle « une fois par personne » est appliquée par le SERVEUR (422 de
  // create-booking-checkout, rejet de confirm_booking, garde du RPC). Cette
  // phrase ne fait que l'expliquer.
  'trial.consumed':
    'Tu as déjà utilisé ta première séance. Cette offre est limitée à une fois par personne.',

  // --- /reserve/coaching-perso — l'offre coaching (97-09) -------------------
  //
  // Le titre RÉUTILISE `home.pricing.coaching.title` — même valeur au caractère
  // près. En revanche `home.pricing.coaching.unit` et `home.pricing.trial.unit`
  // ne sont PAS mobilisables ici : leur valeur « la séance » est un LIBELLÉ
  // d'unité de tarif, alors que sur cette page les mêmes mots sont un fragment
  // de prose (« organiser la séance »). Le harnais D-04 les signalait comme
  // fuites sur la version française ; les réutiliser aurait fait dire à une
  // clé d'unité une chose qu'elle ne dit pas, et cassé la page anglaise le jour
  // où l'unité changerait.
  'coaching.eyebrow': 'COACHING PERSO',
  // Même découpage sur `{price}` que `trial.lead` — le prix est stylé par un
  // `<span>` au milieu de la phrase.
  'coaching.lead': "Une séance d'1h encadrée par un coach certifié — {price}.",
  // Le délai de 48h est une RÈGLE MÉTIER appliquée côté serveur (`slot_too_soon`
  // dans reserve_slot), pas une formule commerciale : l'anglais doit la dire
  // aussi précisément, sans quoi un client anglophone la découvre au refus.
  'coaching.notice':
    "À réserver au moins 48h à l'avance ; le coach te contacte pour organiser la séance.",

  // --- /booking/confirmed/:id — la confirmation de créneau (97-09) ----------
  //
  // Quatre états, quatre familles de clés. Trois CTA sont RÉUTILISÉS plutôt que
  // jumelés — copies exactes, apostrophe comprise :
  //   · `sessionSuccess.paid.dashboardCta` (« Voir mon tableau de bord »)
  //   · `sessionSuccess.dashboardCta`      (« Tableau de bord », deux états)
  //   · `notFound.home`                    (« Retour à l'accueil »)
  // Ce dernier est vérifié à l'octet : cette page porte l'apostrophe DROITE,
  // comme `notFound.home` — contrairement à `sessionSuccess.paid.homeCta`, qui
  // porte la typographique et à qui la mutualisation était refusée en 97-04.
  //
  // ⚠ `confirmation.polling.aria` est un `aria-label`, et c'est le genre de
  // chaîne que les deux détecteurs manquent : « Confirmation en cours » n'a
  // AUCUN accent (le scan lexical ne la voit pas) et vit dans un attribut (le
  // texte visible ne la voit pas). Elle n'est attrapée que parce qu'elle est
  // AU CATALOGUE et que le harnais scanne aussi les attributs.
  'confirmation.polling.aria': 'Confirmation en cours',
  'confirmation.polling.title': 'Confirmation en cours…',
  'confirmation.polling.body': 'Ta réservation est en cours de traitement.',
  'confirmation.confirmed.eyebrow': 'Créneau confirmé',
  'confirmation.confirmed.title': 'Réservation confirmée',
  // T-97-22 — le code PIN est une DONNÉE. Cette phrase ANNONCE l'envoi du PIN,
  // elle n'en porte aucun, et aucune clé de ce catalogue ne doit en porter.
  'confirmation.confirmed.body': "Ton PIN d'accès te sera envoyé par email avant la séance.",
  'confirmation.timeout.title': 'Réservation enregistrée',
  'confirmation.timeout.body':
    'Ta réservation est enregistrée. Tu recevras un email de confirmation sous peu.',
  'confirmation.error.title': 'Confirmation impossible',
  'confirmation.error.body':
    'Une erreur est survenue. Vérifie ton tableau de bord ou contacte-nous.',
} as const
